import { useLazyQuery } from "@apollo/client";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { notification } from "antd";
import { routers } from "config/routes";
import { GET_ACCOUNTING_FIRM, GET_COMPANIES_QUERY } from "queries/centralApi";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { validateId } from "utils/validateId";
import useGlobalStore from "./useGlobalStore";
import useUserDataStore from "./useUserDataStore";

export default function useCompanySelection() {
  const { t } = useTranslation();
  const { companyId, accountingFirmId, menu } = useParams();
  const user = useUserDataStore(state => state.userData);
  const setCurrentCycle = useGlobalStore(state => state.setCurrentCycle);

  const companySelect = useUserDataStore(state => state.companySelect);
  const accountingFirmSelect = useUserDataStore(state => state.accountingFirmSelect);
  const setCompanySelect = useUserDataStore(state => state.setCompanySelect);
  const setAccountingFirmSelect = useUserDataStore(state => state.setAccountingFirmSelect);
  const setCompanyData = useUserDataStore(state => state.setCompanyData);
  const companyIdSelect = companySelect?.id;
  const setListCycles = useGlobalStore(state => state.setListCycles);

  const [getCompany, { data: getCompanyData, error: getCompanyError }] = useLazyQuery<
    GetCompanyData,
    GetCompanyVars
  >(GET_COMPANIES_QUERY, {
    client: clientCentralApiStrawberry,
  });

  useEffect(() => {
    if (getCompanyError) {
      routers.navigate(`/error`, { replace: true });
    }
  }, [getCompanyError]);

  useEffect(() => {
    if (!getCompanyData) return;
    const { getCompany, getCurrentCycle, listCyclesPaginated } = getCompanyData;
    setListCycles(listCyclesPaginated.results);
    setCurrentCycle(getCurrentCycle ? getCurrentCycle.id : undefined);
    if (getCompany) {
      const { accountingFirm } = getCompany;
      setCompanyData(getCompany);
      setCompanySelect({
        id: getCompany.id,
        name: getCompany.name,
        internalReference: getCompany.internalReference,
      });
      if (accountingFirm) {
        setAccountingFirmSelect({ id: accountingFirm.id, name: accountingFirm.name });
      }
      if (companyId !== getCompany.id) {
        routers.navigate(`/company/${getCompany.id}/${menu || "expense"}`, { replace: true });
      }
    } else {
      notification.error({
        message: t("Company not found"),
        description: t("This company does not exist or you do not have access to it."),
        placement: "bottomLeft",
      });
      routers.navigate(`/`, { replace: true });
    }
  }, [getCompanyData]);

  const [getAccountingFirm, { data: getAccountingFirmData, error: getAccountingFirmError }] =
    useLazyQuery<GetAccountingFirmResponseType, GetAccountingFirmInputType>(GET_ACCOUNTING_FIRM, {
      client: clientCentralApiStrawberry,
    });

  useEffect(() => {
    if (getAccountingFirmError) {
      routers.navigate(`/error`, { replace: true });
    }
  }, [getAccountingFirmError]);

  useEffect(() => {
    if (!getAccountingFirmData) return;
    const result = getAccountingFirmData.getAccountingFirm;
    if (!result) {
      notification.error({
        message: t("Accounting firm not found"),
        description: t("This accounting firm does not exist or you do not have access to it."),
        placement: "bottomLeft",
      });
      routers.navigate(`/`, { replace: true });
      return;
    }
    setAccountingFirmSelect({ id: result.id, name: result.name });
    const { companies } = result;
    if (companies && companies.length === 1) {
      getCompany({
        variables: {
          id: companies[0].id,
          company: companies[0].id,
        },
      });
    } else {
      setCompanySelect(undefined);
      setCompanyData(undefined);
    }
  }, [getAccountingFirmData]);

  useEffect(() => {
    if (user) {
      const { companies, accountingFirms } = user;
      const companyID = (companies && companies.length === 1 && companies[0]) || companyId;
      const accountingFirmID =
        (accountingFirms && accountingFirms.length === 1 && accountingFirms[0]) || accountingFirmId;

      if (companyID && validateId(companyID) && companyID !== companySelect?.id) {
        getCompany({
          variables: {
            id: companyID,
            company: companyID,
          },
        });
      } else if (
        accountingFirmID &&
        validateId(accountingFirmID) &&
        accountingFirmID !== accountingFirmSelect?.id
      ) {
        getAccountingFirm({
          variables: {
            accountingFirmId: accountingFirmID,
          },
        });
      }
    } else {
      routers.navigate(`/error`, { replace: true });
    }
  }, [companyId, accountingFirmId]);

  const saveCompany = async (companyData: CompanySelect): Promise<void> => {
    if (companyData.id && companyData.id !== companyIdSelect) {
      setCurrentCycle(undefined);
      getCompany({
        variables: {
          id: companyData.id,
          company: companyData.id,
        },
      });
    }
  };

  return { saveCompany };
}
